import React, { useCallback, useMemo } from 'react'

import { FieldInputProps, useField } from 'formik'

import { CheckboxFieldProps, FieldSettings, FormHelpersProviderReturn } from '../../../ana-toolbox'
import { getHasError } from '../../utils/helpers/getHasError'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { Checkbox } from '../../utils/shared/Checkbox'
import { combinedFields } from '../../utils/yup/main'
import { Translate } from '../translate/translate'

import * as S from './checkbox-field.styles'

const CheckboxFieldBase = ({
  label,
  name,
  error,
  helperText,
  value,
  onChange,
  ...rest
}: CheckboxFieldProps & FieldInputProps<boolean>) => {
  return (
    <>
      <S.FormControlLabel
        error={Boolean(error)}
        label={<Translate>{label}</Translate>}
        control={<Checkbox name={name} checked={value || false} onChange={onChange} inputProps={rest} />}
      />
      <S.FieldHelperText name={name} error={error} helperText={helperText} />
    </>
  )
}

export const CheckboxField = (props: CheckboxFieldProps) => {
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()
  const [field, meta] = useField(props.name)

  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldSettings?.hooks?.beforeChange?.(e.target.checked, formHelpers as FormHelpersProviderReturn, e)
      field.onChange({ target: { value: e.target.checked, name: e.target.name } })
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return <CheckboxFieldBase error={hasError ? meta.error : undefined} {...props} {...field} onChange={handleChange} />
}
